/**
 * Some types of possible point attribute data formats
 *
 * @class
 */
const PointAttributeTypes = {
  DATA_TYPE_DOUBLE: { ordinal: 0, name: 'double', size: 8 },
  DATA_TYPE_FLOAT: { ordinal: 1, name: 'float', size: 4 },
  DATA_TYPE_INT8: { ordinal: 2, name: 'int8', size: 1 },
  DATA_TYPE_UINT8: { ordinal: 3, name: 'uint8', size: 1 },
  DATA_TYPE_INT16: { ordinal: 4, name: 'int16', size: 2 },
  DATA_TYPE_UINT16: { ordinal: 5, name: 'uint16', size: 2 },
  DATA_TYPE_INT32: { ordinal: 6, name: 'int32', size: 4 },
  DATA_TYPE_UINT32: { ordinal: 7, name: 'uint32', size: 4 },
  DATA_TYPE_INT64: { ordinal: 8, name: 'int64', size: 8 },
  DATA_TYPE_UINT64: { ordinal: 9, name: 'uint64', size: 8 },
} as const

let i = 0
for (const obj in PointAttributeTypes) {
  PointAttributeTypes[i] = PointAttributeTypes[obj]
  i++
}

export { PointAttributeTypes }

/**
 * 单项点属性描述，记录名称、数据类型、元素数量与取值范围。
 */
class PointAttribute {
  /** 属性名称（如 "position"、"rgba"） */
  name: string
  /** 基础数据类型描述，含 ordinal/name/size */
  type: { ordinal: number; name: string; size: number }
  /** 单点的元素数量（如 position 为 3） */
  numElements: number
  /** 单点占用字节数 = numElements * type.size */
  byteSize: number
  /** 附加说明 */
  description: string
  /** 运行时统计的取值范围 [min, max] */
  range: [number, number]
  constructor(name, type, numElements) {
    this.name = name
    this.type = type
    this.numElements = numElements
    this.byteSize = this.numElements * this.type.size
    this.description = ''
    this.range = [Infinity, -Infinity]
  }
  /** 位置 (float32×3) */
  static POSITION_CARTESIAN: PointAttribute
  /** RGBA 颜色 (int8×4) */
  static RGBA_PACKED: PointAttribute
  /** COLOR_PACKED 别名，等同 RGBA_PACKED */
  static COLOR_PACKED: PointAttribute
  /** RGB 颜色 (int8×3) */
  static RGB_PACKED: PointAttribute
  /** 法线 (float32×3) */
  static NORMAL_FLOATS: PointAttribute
  /** 强度 (uint16) */
  static INTENSITY: PointAttribute
  /** 分类码 (uint8) */
  static CLASSIFICATION: PointAttribute
  /** 球面映射法线 (uint8×2) */
  static NORMAL_SPHEREMAPPED: PointAttribute
  /** 八面体 16 位法线 (uint8×2) */
  static NORMAL_OCT16: PointAttribute
  /** 浮点法线 (float32×3) */
  static NORMAL: PointAttribute
  /** 回波号 (uint8) */
  static RETURN_NUMBER: PointAttribute
  /** 总回波数 (uint8) */
  static NUMBER_OF_RETURNS: PointAttribute
  /** 点源 ID (uint16) */
  static SOURCE_ID: PointAttribute
  /** 索引 (uint32) */
  static INDICES: PointAttribute
  /** 间距 (float32) */
  static SPACING: PointAttribute
  /** GPS 时间 (double) */
  static GPS_TIME: PointAttribute
}

PointAttribute.POSITION_CARTESIAN = new PointAttribute(
  'POSITION_CARTESIAN',
  PointAttributeTypes.DATA_TYPE_FLOAT,
  3
)

PointAttribute.RGBA_PACKED = new PointAttribute(
  'COLOR_PACKED',
  PointAttributeTypes.DATA_TYPE_INT8,
  4
)

PointAttribute.COLOR_PACKED = PointAttribute.RGBA_PACKED

PointAttribute.RGB_PACKED = new PointAttribute(
  'COLOR_PACKED',
  PointAttributeTypes.DATA_TYPE_INT8,
  3
)

PointAttribute.NORMAL_FLOATS = new PointAttribute(
  'NORMAL_FLOATS',
  PointAttributeTypes.DATA_TYPE_FLOAT,
  3
)

PointAttribute.INTENSITY = new PointAttribute('INTENSITY', PointAttributeTypes.DATA_TYPE_UINT16, 1)

PointAttribute.CLASSIFICATION = new PointAttribute(
  'CLASSIFICATION',
  PointAttributeTypes.DATA_TYPE_UINT8,
  1
)

PointAttribute.NORMAL_SPHEREMAPPED = new PointAttribute(
  'NORMAL_SPHEREMAPPED',
  PointAttributeTypes.DATA_TYPE_UINT8,
  2
)

PointAttribute.NORMAL_OCT16 = new PointAttribute(
  'NORMAL_OCT16',
  PointAttributeTypes.DATA_TYPE_UINT8,
  2
)

PointAttribute.NORMAL = new PointAttribute('NORMAL', PointAttributeTypes.DATA_TYPE_FLOAT, 3)

PointAttribute.RETURN_NUMBER = new PointAttribute(
  'RETURN_NUMBER',
  PointAttributeTypes.DATA_TYPE_UINT8,
  1
)

PointAttribute.NUMBER_OF_RETURNS = new PointAttribute(
  'NUMBER_OF_RETURNS',
  PointAttributeTypes.DATA_TYPE_UINT8,
  1
)

PointAttribute.SOURCE_ID = new PointAttribute('SOURCE_ID', PointAttributeTypes.DATA_TYPE_UINT16, 1)

PointAttribute.INDICES = new PointAttribute('INDICES', PointAttributeTypes.DATA_TYPE_UINT32, 1)

PointAttribute.SPACING = new PointAttribute('SPACING', PointAttributeTypes.DATA_TYPE_FLOAT, 1)

PointAttribute.GPS_TIME = new PointAttribute('GPS_TIME', PointAttributeTypes.DATA_TYPE_DOUBLE, 1)

export { PointAttribute }

/**
 * 点属性集合，聚合多个 PointAttribute 并维护总字节数。
 */
export class PointAttributes {
  /** 聚合的属性列表 */
  attributes: PointAttribute[]
  /** 单点总字节数 */
  byteSize: number
  /** 属性数量 */
  size: number
  /** 向量组合定义，如将 NormalX/Y/Z 聚合为 NORMAL */
  vectors: { name: string; attributes: string[] }[]
  constructor(pointAttributes?: string[]) {
    this.attributes = []
    this.byteSize = 0
    this.size = 0
    this.vectors = []

    if (pointAttributes != null) {
      for (let i = 0; i < pointAttributes.length; i++) {
        const pointAttributeName = pointAttributes[i]
        const pointAttribute = PointAttribute[pointAttributeName]
        this.attributes.push(pointAttribute)
        this.byteSize += pointAttribute.byteSize
        this.size++
      }
    }
  }

  /**
   * 追加一个属性，同步更新 byteSize 与 size。
   *
   * @param pointAttribute - 待加入的属性
   */
  add(pointAttribute: PointAttribute) {
    this.attributes.push(pointAttribute)
    this.byteSize += pointAttribute.byteSize
    this.size++
  }

  /**
   * 追加一个向量组合定义。
   *
   * @param vector - 含 name 与 attributes 字段，描述组合关系
   */
  addVector(vector: { name: string; attributes: string[] }) {
    this.vectors.push(vector)
  }

  /**
   * 判断当前属性集合是否包含法线。
   *
   * @returns 存在任一法线属性返回 true
   */
  hasNormals() {
    for (const name in this.attributes) {
      const pointAttribute = this.attributes[name]
      if (
        pointAttribute === PointAttribute.NORMAL_SPHEREMAPPED ||
        pointAttribute === PointAttribute.NORMAL_FLOATS ||
        pointAttribute === PointAttribute.NORMAL ||
        pointAttribute === PointAttribute.NORMAL_OCT16
      ) {
        return true
      }
    }

    return false
  }
}
